import { Session } from "@shared/schema";
import { TherapistWithSpecialties } from "@shared/schema";
import { Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
} from "@/components/ui/dialog";

interface SessionExtended extends Session {
  therapist: TherapistWithSpecialties;
}

interface SessionCardProps {
  session: SessionExtended;
}

export default function SessionCard({ session }: SessionCardProps) {
  const { toast } = useToast();
  
  const cancelMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PATCH", `/api/sessions/${session.id}`, {
        status: "cancelled"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      toast({
        title: "Sessão cancelada",
        description: "Sua sessão foi cancelada com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao cancelar",
        description: "Não foi possível cancelar a sessão. Tente novamente.",
        variant: "destructive",
      });
    }
  });
  
  const formatDate = (date: Date | string) => {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatTime = (date: Date | string) => {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case "scheduled":
        return "Agendada";
      case "completed":
        return "Realizada";
      case "cancelled":
        return "Cancelada";
      default:
        return status;
    }
  };
  
  const getSessionTypeClass = (isFree: boolean) => {
    return isFree ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800";
  };
  
  const getSessionTypeText = (isFree: boolean) => {
    return isFree ? "Gratuita" : "Adicional";
  };
  
  const endTime = new Date(new Date(session.date).getTime() + 60 * 60 * 1000);
  
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-8 w-8">
            <img className="h-8 w-8 rounded-full" src={session.therapist.image_url} alt={session.therapist.name} />
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">
              {session.therapist.name}
            </div>
            <div className="text-xs text-gray-500">
              {session.therapist.title}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{formatDate(session.date)}</div>
        <div className="text-xs text-gray-500">{formatTime(session.date)} - {formatTime(endTime)}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSessionTypeClass(session.isFree)}`}>
          {getSessionTypeText(session.isFree)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(session.status)}`}>
          {getStatusText(session.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
        {session.status === "scheduled" && (
          <>
            {session.meetingLink && (
              <a 
                href={session.meetingLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:text-secondary"
              >
                Entrar
              </a>
            )}
            <Dialog>
              <DialogTrigger asChild>
                <button className="text-gray-500 hover:text-red-500">Cancelar</button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancelar sessão</DialogTitle>
                  <DialogDescription>
                    Tem certeza que deseja cancelar a sessão com {session.therapist.name} 
                    agendada para {formatDate(session.date)} às {formatTime(session.date)}?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="destructive"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? "Cancelando..." : "Confirmar cancelamento"}
                  </Button>
                  <Button variant="outline" asChild>
                    <DialogTrigger>Voltar</DialogTrigger>
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        
        {session.status === "completed" && (
          <>
            <a href="#" className="text-primary hover:text-secondary">Relatório</a>
            <Link href={`/booking?therapistId=${session.therapistId}`} className="text-gray-500 hover:text-primary">
              Agendar nova
            </Link>
          </>
        )}
        
        {session.status === "cancelled" && (
          <Link href={`/booking?therapistId=${session.therapistId}`} className="text-gray-500 hover:text-primary">
            Agendar nova
          </Link>
        )}
      </td>
    </tr>
  );
}
